<template>
    <div class="bg-white rounded-lg shadow-sm p-3 sm:p-6 xl:p-8 overflow-hidden">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
            <h2 class="text-2xl xl:text-3xl font-header font-bold text-primary">Calendar</h2>
            <div class="flex items-center gap-2 sm:gap-4">
                <button @click="previousMonth" class="p-2 hover:bg-gray-100 rounded-md transition-colors"
                    aria-label="Previous month">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <span class="text-base sm:text-lg font-header font-semibold text-primary min-w-[120px] sm:min-w-[180px] text-center">
                    {{ currentMonthYear }}
                </span>
                <button @click="nextMonth" class="p-2 hover:bg-gray-100 rounded-md transition-colors"
                    aria-label="Next month">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>

        <div class="grid grid-cols-7 gap-1 sm:gap-2 mb-4">
            <div v-for="day in weekDays" :key="day"
                class="text-center text-xs sm:text-sm font-header font-semibold text-gray-600 py-1 sm:py-2">
                {{ day }}
            </div>
        </div>

        <div class="grid grid-cols-7 gap-1 sm:gap-2">
            <div v-for="day in calendarDays" :key="day.date" @click="day.hasEvent && selectDay(day)" :class="[
                'relative p-1 sm:p-2 xl:p-3 rounded-md text-center cursor-pointer transition-all',
                day.isCurrentMonth ? 'hover:bg-gray-50' : 'text-gray-300',
                day.isToday ? 'bg-accent/10 font-bold' : '',
                day.hasEvent ? 'cursor-pointer hover:shadow-md' : 'cursor-default',
                day.selected ? 'ring-2 ring-accent' : ''
            ]">
                <span :class="[
                    'text-xs sm:text-sm xl:text-base font-body',
                    day.isToday ? 'text-accent' : day.isCurrentMonth ? 'text-primary' : 'text-gray-300',
                    day.hasEvent ? 'font-semibold' : ''
                ]">
                    {{ day.day }}
                </span>
                <div v-if="day.hasEvent" class="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                    <div :class="[
                        'w-1.5 h-1.5 rounded-full',
                        getEventProximityClass(day.eventDate)
                    ]"></div>
                </div>
                <div v-if="day.eventCount > 1" class="absolute top-1 right-1 text-xs font-semibold text-accent">
                    +{{ day.eventCount - 1 }}
                </div>
            </div>
        </div>
    </div>

    <Teleport to="body">
        <Transition name="modal">
            <div v-if="showEventModal && selectedDayEvents.length > 0" ref="modalRef"
                class="event-modal-overlay fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                @click.self="closeModal">
                <div class="event-modal-content bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
                    data-modal-content>
                    <button @click="closeModal"
                        class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
                        aria-label="Close">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div class="bg-white p-6 xl:p-8 relative">
                        <div v-if="isTranslatingModal"
                            class="absolute inset-0 bg-white flex items-center justify-center z-10">
                            <Loader size="large" />
                        </div>
                        <div :class="{ 'opacity-0': isTranslatingModal }">
                            <h3 class="text-xl xl:text-2xl font-header font-bold text-primary mb-6">
                                Events on {{ formatSelectedDate(selectedDay?.date) }}
                            </h3>
                            <div class="space-y-4">
                                <div v-for="event in selectedDayEvents" :key="event.id" @click="goToEvent(event.id)"
                                    class="p-5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors border-l-4 border-accent">
                                    <h4 class="font-header font-semibold text-primary mb-2 text-lg" data-no-translate>
                                        {{ event.title }}
                                    </h4>
                                    <div class="flex flex-wrap items-center gap-3 text-sm text-gray-600 font-body">
                                        <div class="flex items-center gap-1">
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span>{{ event.time }}</span>
                                        </div>
                                        <div class="flex items-center gap-1">
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            <span>{{ event.location }}</span>
                                        </div>
                                    </div>
                                    <div v-if="event.description"
                                        class="mt-3 text-sm text-gray-700 font-body line-clamp-2"
                                        v-html="event.description">
                                    </div>
                                    <div v-if="event.rsvp" class="mt-3">
                                        <span
                                            class="inline-block px-3 py-1 bg-accent text-white rounded text-xs font-body font-semibold">
                                            Tickets Available
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { translationService } from '../services/translation'
import Loader from './Loader.vue'
import { getESTToday, convertToESTDate } from '../utils/date'

const props = defineProps({
    events: {
        type: Array,
        required: true
    }
})

const emit = defineEmits(['day-selected'])

const currentDate = ref(new Date())
const selectedDay = ref(null)
const showEventModal = ref(false)
const isTranslatingModal = ref(false)
const modalRef = ref(null)

const waitForDOMUpdate = () => {
    return new Promise(resolve => {
        requestAnimationFrame(() => {
            requestAnimationFrame(resolve)
        })
    })
}

const translateModal = async () => {
    const currentLang = translationService.getLanguage()
    if (currentLang === 'en') {
        isTranslatingModal.value = false
        return
    }

    isTranslatingModal.value = true
    await nextTick()
    await waitForDOMUpdate()

    if (modalRef.value) {
        try {
            await new Promise(resolve => setTimeout(resolve, 100))
            await translationService.translateElement(modalRef.value)
            await nextTick()
            await waitForDOMUpdate()
        } catch (error) {
            console.error('Error translating modal:', error)
        } finally {
            isTranslatingModal.value = false
        }
    } else {
        isTranslatingModal.value = false
    }
}

const handleLanguageChange = async () => {
    if (showEventModal.value) {
        await translateModal()
    }
}

watch(showEventModal, async (isOpen) => {
    if (isOpen) {
        const currentLang = translationService.getLanguage()
        if (currentLang === 'en') {
            isTranslatingModal.value = false
        } else {
            await nextTick()
            await waitForDOMUpdate()
            await translateModal()
        }
    }
})

onMounted(() => {
    window.addEventListener('language-changed', handleLanguageChange)
})

onUnmounted(() => {
    window.removeEventListener('language-changed', handleLanguageChange)
})

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const currentMonthYear = computed(() => {
    const formatter = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'America/New_York'
    })
    return formatter.format(currentDate.value)
})

const calendarDays = computed(() => {
    const estFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        weekday: 'short'
    })

    const currentParts = estFormatter.formatToParts(currentDate.value)
    const year = parseInt(currentParts.find(p => p.type === 'year').value)
    const month = parseInt(currentParts.find(p => p.type === 'month').value) - 1

    const firstDayESTStr = createESTDateString(year, month + 1, 1)
    const firstDayEST = convertToESTDate(firstDayESTStr)
    if (!firstDayEST) return []

    const firstDayParts = estFormatter.formatToParts(firstDayEST)
    const firstDayWeekday = firstDayParts.find(p => p.type === 'weekday').value

    const weekdayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 }
    const dayOfWeek = weekdayMap[firstDayWeekday] || 0

    const startOfWeek = new Date(firstDayEST)
    startOfWeek.setUTCDate(startOfWeek.getUTCDate() - dayOfWeek)

    const days = []
    const today = getESTToday()

    for (let i = 0; i < 42; i++) {
        const calendarDate = new Date(startOfWeek)
        calendarDate.setUTCDate(startOfWeek.getUTCDate() + i)

        const parts = estFormatter.formatToParts(calendarDate)
        const estYear = parseInt(parts.find(p => p.type === 'year').value)
        const estMonth = parseInt(parts.find(p => p.type === 'month').value)
        const estDay = parseInt(parts.find(p => p.type === 'day').value)

        const dateStr = createESTDateString(estYear, estMonth, estDay)
        const dateEST = convertToESTDate(dateStr)
        if (!dateEST) continue

        const dayEvents = props.events.filter(event => {
            if (!event.date?.fullDate) return false
            const eventDate = convertToESTDate(event.date.fullDate)
            if (!eventDate || !dateEST) return false
            return eventDate.getTime() === dateEST.getTime()
        })

        const isToday = dateEST.getTime() === today.getTime()
        const isCurrentMonth = estMonth - 1 === month && estYear === year

        days.push({
            date: dateStr,
            day: estDay,
            isCurrentMonth,
            isToday,
            hasEvent: dayEvents.length > 0,
            eventCount: dayEvents.length,
            eventDate: dayEvents.length > 0 ? dayEvents[0].date.fullDate : null,
            selected: selectedDay.value?.date === dateStr
        })
    }

    return days
})

const selectedDayEvents = computed(() => {
    if (!selectedDay.value) return []
    return props.events.filter(event => {
        if (!event.date?.fullDate) return false
        const eventDate = convertToESTDate(event.date.fullDate)
        const selectedDate = convertToESTDate(selectedDay.value.date)
        if (!eventDate || !selectedDate) return false
        return eventDate.getTime() === selectedDate.getTime()
    })
})

function createESTDateString(year, month, day) {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function formatSelectedDate(dateStr) {
    if (!dateStr) return ''
    const dateEST = convertToESTDate(dateStr)
    if (!dateEST) return dateStr

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
    return formatter.format(dateEST)
}

function getEventProximityClass(eventDate) {
    if (!eventDate) return ''
    const event = convertToESTDate(eventDate)
    if (!event) return ''

    const today = getESTToday()

    const diffTime = event - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 0) return 'bg-gray-400'
    if (diffDays <= 7) return 'bg-red-500'
    if (diffDays <= 30) return 'bg-yellow-500'
    return 'bg-green-500'
}

function selectDay(day) {
    if (!day.hasEvent) return
    selectedDay.value = day
    showEventModal.value = true
    emit('day-selected', day.date)
}

function closeModal() {
    showEventModal.value = false
    selectedDay.value = null
    isTranslatingModal.value = false
}

function previousMonth() {
    currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() - 1, 1)
    selectedDay.value = null
    showEventModal.value = false
    isTranslatingModal.value = false
}

function nextMonth() {
    currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 1)
    selectedDay.value = null
    showEventModal.value = false
    isTranslatingModal.value = false
}

function goToEvent(eventId) {
    window.location.href = `/events/${eventId}`
}
</script>

<style scoped>
.event-modal-overlay {
    backdrop-filter: blur(2px);
}

.event-modal-content {
    background-color: #FFFFFF !important;
}

.event-modal-content>div {
    background-color: #FFFFFF !important;
}

.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.2s ease;
}

.modal-enter-active .event-modal-content,
.modal-leave-active .event-modal-content {
    transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-from .event-modal-content,
.modal-leave-to .event-modal-content {
    transform: scale(0.95);
    opacity: 0;
}
</style>
